import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Grid,
  Skeleton,
  Typography,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import './Home.scss';
import ButtonAppBar from 'Layouts/ButtonAppBar/ButtonAppBar';
import { recipeService } from 'Services/recipeService';
import { RecipeWithAuthor } from 'Types/api';

const Home = () => {
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState<RecipeWithAuthor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const fetchRecipes = async () => {
      try {
        const response = await recipeService.getPublic(8);
        setRecipes(response.items);
      } catch {
        // Silently fail - home page can work without featured recipes
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecipes();
  }, []);

  return (
    <div className='container'>
      <ButtonAppBar />
      <Container maxWidth='lg'>
        <Box component='main' sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3 } }}>
            <Typography
              variant='h5'
              component='h1'
              fontWeight={600}
              sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
            >
              Discover Recipes
            </Typography>
          </Box>

          {isLoading ? (
            <RecipeGridSkeleton />
          ) : recipes.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color='text.secondary'>
                No recipes available yet. Check back soon!
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {recipes.map((recipe) => (
                <Grid item xs={6} sm={4} md={3} key={recipe.id}>
                  <RecipeCard recipe={recipe} onClick={() => navigate(`/recipes/${recipe.id}`)} />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Container>
    </div>
  );
};

const RecipeCard = ({ recipe, onClick }: { recipe: RecipeWithAuthor; onClick: () => void }) => {
  const totalTime = recipe.prepTime + recipe.cookTime;

  return (
    <Card variant='outlined' sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardActionArea onClick={onClick} sx={{ flexGrow: 1 }}>
        {recipe.imageUrl ? (
          <CardMedia component='img' height='120' image={recipe.imageUrl} alt={recipe.title} />
        ) : (
          <Box
            sx={{
              height: 120,
              bgcolor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <RestaurantIcon sx={{ fontSize: 48, color: 'grey.400' }} />
          </Box>
        )}
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography
            variant='subtitle2'
            component='h3'
            sx={{
              mb: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              lineHeight: 1.3,
              minHeight: '2.6em',
            }}
          >
            {recipe.title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.75 }}>
            <Chip
              icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
              label={`${totalTime}m`}
              size='small'
              variant='outlined'
              sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
            />
            <Chip
              icon={<RestaurantIcon sx={{ fontSize: 14 }} />}
              label={recipe.servings}
              size='small'
              variant='outlined'
              sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
            />
          </Box>
          {recipe.author && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Avatar
                src={recipe.author.avatarUrl}
                sx={{ width: 18, height: 18, fontSize: '0.6rem' }}
              >
                {recipe.author.displayName?.[0]?.toUpperCase() || '?'}
              </Avatar>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {recipe.author.displayName || 'Chef'}
              </Typography>
            </Box>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

const RecipeGridSkeleton = () => (
  <Grid container spacing={2}>
    {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
      <Grid item xs={6} sm={4} md={3} key={i}>
        <Card variant='outlined'>
          <Skeleton variant='rectangular' height={120} />
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Skeleton variant='text' width='90%' height={20} />
            <Skeleton variant='text' width='70%' height={20} />
            <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
              <Skeleton variant='rounded' width={50} height={22} />
              <Skeleton variant='rounded' width={40} height={22} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

export default Home;
